/**
 * The encounter→roster **commit extractor**.
 *
 * The deliberate *other* crossing of the spawn seam: deploy reads roster → mints a combatant
 * (`spawnMech`); commit reads a post-fight combatant → mints the **sparse** writeback payload a
 * roster `apply_printed_mech_state` command stores. It lives in `@lancer-vtt/rules` (not the
 * domain reducer) because it needs `compute` to re-derive the blueprint maxima (so the stored
 * record stays sparse — pristine fields dropped); the reducer stays pure and reference-free.
 *
 * **Commit is explicit + one-directional**: nothing here auto-couples to "encounter ended" — a
 * caller (the CLI `commit` verb, later a GM panel) runs this per committable mech and
 * issues the roster command. **Only a mech-kind combatant spawned from a printed mech is
 * committable**; this skips (never faults) an NPC, a combatant lacking the spawn breadcrumb, or
 * one whose target pilot/mech **no longer exists** (a dangling ref — the mech may have been
 * deleted/reprinted mid-fight). **A combatant that ended destroyed is
 * committed, not skipped**: the payload carries `destroyed: "wreck" |
 * "vaporized"` (keyed off the `Vaporized` second-status, `VAPORIZED_STATUS`) and drops the
 * attrition track fields (`hp`/`structure`/`stress`/`repairsRemaining`) entirely — they're
 * meaningless mid-destruction; the recovery verbs (`restore_destroyed_mech`/`full_repair`) set
 * them by RAW regardless of what they were at the moment of death.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { ApplyNpcWearCommand, ApplyPrintedMechStateCommand, Combatant, EncounterState, NpcWear, PilotData, PrintedMechState, RosterBatchCommand, RosterState, SetPilotHpCommand } from "@lancer-vtt/domain";
import {
  EJECT_LOCKOUT_RESOURCE,
  REPAIR_CAP_RESOURCE,
  destroyedItemKeys,
  limitedUsedCounts,
  limitedSystemUsedCounts,
  hasStatus,
  resolvePrintedMechValues,
  sparsePrintedMechState,
  type PrintedMechMaxes,
  type PrintedMechValues,
} from "@lancer-vtt/domain";
import { compute } from "../compute";
import { VAPORIZED_STATUS } from "./wreckConversion";

/**
 * Re-derive a printed mech's **blueprint maxima** live: HP +
 * Repair Cap from `compute`, the structure/stress consequence tracks off the frame (the same
 * reads `spawnMech` uses). The single source both the commit extractor and the roster `repair`
 * verb feed into the maxima-carrying `spend_repairs`/sparseness logic. `null` when the mech /
 * its frame no longer resolves (a dangling ref the caller skips). Pure over the current
 * blueprint — editing a build mid-mission re-stats the printed mech (the accepted cost).
 */
export function printedMechMaxes(args: {
  pilot: PilotData;
  mechId: string;
  compendium: Compendium;
}): PrintedMechMaxes | null {
  const { pilot, mechId, compendium } = args;
  const mech = pilot.mechs.find((m) => m.id === mechId);
  if (!mech) return null;
  const { mechs } = compute(pilot, compendium);
  const computed = mechs.find((m) => m.id === mechId);
  if (!computed) return null;
  const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  return {
    maxHp: computed.stats.hp,
    maxStructure: typeof frame?.stats.structure === "number" ? frame.stats.structure : 4,
    maxStress: typeof frame?.stats.stress === "number" ? frame.stats.stress : 4,
    repairCapMax: computed.stats.repcap,
    // `compute`'s frame-trait-bonus-aware structure repair
    // cost (2 by default, 1 for the Everest's "Replaceable parts") — threaded through so a
    // `spend_repairs` command built off these maxes (`roster-cli.ts`'s `vRepair`, which spreads
    // `...maxes`) carries the override for free.
    structureRepairCost: computed.stats.structureRepairCost,
  };
}

/** A committable mech's extracted writeback payload — the sparse fields an `apply_printed_mech_state` carries. */
export type CommitPayload = {
  committed: true;
  pilotId: string;
  mechId: string;
  hp?: number;
  structure?: number;
  stress?: number;
  repairsRemaining?: number;
  /** Total-destruction marker — set instead of the track fields above. */
  destroyed?: "wreck" | "vaporized";
  /**
   * Eject-lockout persistence marker — set when the committed combatant carries the live
   * Eject-lockout resource, independent of `destroyed`/the ordinary attrition fields (Eject
   * persistence and HP/structure/stress attrition are orthogonal).
   */
  ejectSpent?: true;
  /**
   * **Destroyed-item persistence** — the committed
   * combatant's live `destroyed:<key>` resource markers (read via `destroyedItemKeys`), sparse
   * (omitted when empty). **Never set alongside `destroyed`** — a wreck/vaporized combatant
   * returns early above with no track fields at all; its items are moot the same way.
   */
  destroyedItems?: string[];
  /**
   * **Limited-weapon consumed-charge persistence** (replenished by
   * `rest_limited_charge_replenish`) — the committed combatant's live consumed-charge counts
   * from `current.affordanceState`, keyed by `weaponInstanceKey` (read via `limitedUsedCounts`),
   * sparse (omitted when empty). Same
   * "never set alongside `destroyed`" rule as {@link destroyedItems} — a wreck/vaporized
   * commit returns early below with no track fields at all.
   */
  limitedUsed?: Record<string, number>;
  /**
   * **Limited-SYSTEM consumed-charge persistence** — the committed combatant's live
   * `current.affordanceState[limited.pool ?? key].uses` counts for granted actions whose frozen
   * affordance record carries a `limited` pool (read via `limitedSystemUsedCounts`, which skips an
   * ordinary RAW "1/scene" ability's `cap` rate), sparse (omitted when empty). Same "never set alongside `destroyed`" rule as {@link limitedUsed}.
   */
  limitedSystemUsed?: Record<string, number>;
}

/** A non-committable combatant: the reason it was skipped (for a CLI warn line). */
export type CommitSkip = {
  committed: false;
  /** A short, human reason ("not a mech", "ended destroyed", "dangling mech ref", …). */
  reason: string;
}

export type CommitOutcome = CommitPayload | CommitSkip;

const skip = (reason: string): CommitSkip => ({ committed: false, reason });

/**
 * Extract a printed mech's post-fight writeback payload from a combatant, or a typed skip.
 * Re-derives the blueprint maxima from the *current* roster blueprint (the accepted re-stat
 * cost of editing a build mid-mission) so the stored record is sparse. The current
 * values come off the combatant's frozen-tracked `current` (+ the `repair_cap` resource).
 */
export function extractPrintedMechState(args: {
  combatant: Combatant;
  roster: RosterState;
  compendium: Compendium;
}): CommitOutcome {
  const { combatant, roster, compendium } = args;

  // Committable gate — mech-kind, spawned-from-printed.
  if (combatant.kind !== "mech") return skip(`${combatant.name}: not a mech (ephemeral)`);
  const mechId = combatant.sourcePrintedMechId;
  const pilotId = combatant.pilotId;
  if (!mechId || !pilotId) return skip(`${combatant.name}: no printed-mech breadcrumb (not deployed from a printed mech)`);

  // Dangling-ref tolerance: the pilot/mech may have been deleted or reprinted mid-fight.
  const pilot = roster.pilots[pilotId];
  if (!pilot) return skip(`${combatant.name}: source pilot ${pilotId} no longer exists (dangling — skipped)`);
  const maxes = printedMechMaxes({ pilot, mechId, compendium });
  if (!maxes) return skip(`${combatant.name}: source mech ${mechId} no longer exists / frame unresolved (dangling — skipped)`);

  // Total-destruction marker: a destroyed mech is COMMITTED, not skipped — the attrition track
  // fields are dropped entirely (meaningless mid-destruction).
  if (hasStatus(combatant.statuses, "Destroyed")) {
    const destroyed = hasStatus(combatant.statuses, VAPORIZED_STATUS) ? "vaporized" : "wreck";
    return { committed: true, pilotId, mechId, destroyed };
  }

  // Current values off the (already-clamped) combatant; the Repair Cap remaining rides the
  // generic resource (default to full cap if the combatant predates the seeded resource).
  const cur = {
    hp: combatant.current.hp,
    structure: combatant.current.structure ?? maxes.maxStructure,
    stress: combatant.current.stress ?? maxes.maxStress,
    repairsRemaining: combatant.current.resources?.[REPAIR_CAP_RESOURCE] ?? maxes.repairCapMax,
  };
  const record = sparsePrintedMechState(cur, maxes);

  // Eject-lockout persistence — independent of the track fields above.
  const ejectSpent = (combatant.current.resources?.[EJECT_LOCKOUT_RESOURCE] ?? 0) >= 1;

  // Destroyed-item persistence — sparse, omitted when empty.
  const destroyedItems = destroyedItemKeys(combatant.current.resources);

  // Limited-weapon consumed-charge persistence — sparse, omitted when empty (every charge
  // unspent, the ordinary case).
  const limitedUsed = limitedUsedCounts(combatant.current.affordanceState, combatant.base.weapons);

  // Limited-SYSTEM consumed-charge persistence — sparse, omitted when empty. Reads only the frozen
  // affordance record's `limited` pool, so an ordinary RAW "1/scene" rate's consumed scene-use
  // count is NEVER captured here (it resets for free every fresh encounter).
  const limitedSystemUsed = limitedSystemUsedCounts(combatant.current.affordanceState, combatant.base.affordances);

  return {
    committed: true,
    pilotId,
    mechId,
    ...(record ?? {}),
    ...(ejectSpent ? { ejectSpent: true as const } : {}),
    ...(destroyedItems.length > 0 ? { destroyedItems } : {}),
    ...(Object.keys(limitedUsed).length > 0 ? { limitedUsed } : {}),
    ...(Object.keys(limitedSystemUsed).length > 0 ? { limitedSystemUsed } : {}),
  };
}

/** One side of a commit row: the carried tracks resolved to full values, plus the wear markers. */
export type CommitRowValues = PrintedMechValues & {
  destroyed?: "wreck" | "vaporized";
  destroyedItems: string[];
};

/** One committed mech's before → after, for a CLI line or the GM's END COMBAT panel. */
export type CommitRow = {
  combatantId: string;
  combatantName: string;
  pilotId: string;
  mechId: string;
  /** The roster's printed record before this commit. */
  before: CommitRowValues;
  /** What the commit writes. */
  after: CommitRowValues;
  /** The extracted payload the row's `apply_printed_mech_state` carries. */
  payload: CommitPayload;
};

/** A keyed NPC's tracks resolved to full values (absent ⇒ max), plus the destroyed marker. */
export type NpcWearValues = { hp: number; structure: number; stress: number; destroyed: boolean };

/** One keyed allied NPC's before → after (`decisions/0196`). */
export type NpcCommitRow = {
  combatantId: string;
  combatantName: string;
  /** The `RosterState.npcWear` key (`Combatant.persistentNpcKey`). */
  key: string;
  before: NpcWearValues;
  after: NpcWearValues;
  /** The sparse wear the row's `apply_npc_wear` writes (`{}` ⇒ pristine, the entry is removed). */
  wear: NpcWear;
};

/** One roster pilot on foot's HP before → after (`PilotData.currentHp`, absent ⇒ `maxHp`). */
export type PilotCommitRow = {
  combatantId: string;
  combatantName: string;
  pilotId: string;
  maxHp: number;
  before: number;
  after: number;
};

/** A combatant the commit leaves alone, and why. */
export type CommitPlanSkip = {
  combatantId: string;
  reason: string;
};

/** The whole commit, planned but not applied. */
export type CommitPlan = {
  /**
   * The one roster `batch` that commits the encounter: a `record_encounter_commit` followed
   * by one `apply_printed_mech_state` per row. Applying it as a single command makes the
   * commit one undo, filed under `encounterCommitScope(encounterId)`.
   */
  batch: RosterBatchCommand;
  rows: CommitRow[];
  /** The keyed allied NPCs whose wear the batch writes back (one `apply_npc_wear` each). */
  npcRows: NpcCommitRow[];
  /** The roster pilots on foot whose HP the batch writes back (one `set_pilot_hp` each). */
  pilotRows: PilotCommitRow[];
  skipped: CommitPlanSkip[];
  /** When this encounter was last committed, if it was (`RosterState.encounterCommits`). */
  priorCommitAt?: string;
};

function rowValues(record: PrintedMechState | undefined, maxes: PrintedMechMaxes): CommitRowValues {
  return {
    ...resolvePrintedMechValues(record, maxes),
    ...(record?.destroyed !== undefined ? { destroyed: record.destroyed } : {}),
    destroyedItems: record?.destroyedItems ?? [],
  };
}

/** A keyed NPC's current tracks as the ledger's sparse wear: a track at its max is dropped. */
function npcWearOf(c: Combatant): NpcWear {
  if (hasStatus(c.statuses, "Destroyed")) return { destroyed: true };
  const wear: NpcWear = {};
  if (c.current.hp < c.base.maxHp) wear.hp = c.current.hp;
  const maxStructure = c.base.maxStructure ?? 0;
  if ((c.current.structure ?? maxStructure) < maxStructure) wear.structure = c.current.structure;
  const maxStress = c.base.maxStress ?? 0;
  if ((c.current.stress ?? maxStress) < maxStress) wear.stress = c.current.stress;
  return wear;
}

/** Resolve sparse wear against a combatant's maxima, for a row. */
function npcValues(wear: NpcWear | undefined, c: Combatant): NpcWearValues {
  return {
    hp: wear?.hp ?? c.base.maxHp,
    structure: wear?.structure ?? c.base.maxStructure ?? 0,
    stress: wear?.stress ?? c.base.maxStress ?? 0,
    destroyed: wear?.destroyed === true,
  };
}

/** The kinds a commit writes back by kind alone: a printed mech, and a roster pilot on foot. */
const COMMITTED_KINDS: ReadonlySet<Combatant["kind"]> = new Set(["mech", "pilot"]);

function applyCommand(actorId: string, p: CommitPayload): ApplyPrintedMechStateCommand {
  const { committed: _committed, ...fields } = p;
  return { type: "apply_printed_mech_state", actorId, ...fields };
}

/**
 * Plan committing an encounter's printed mechs back to the roster: run
 * {@link extractPrintedMechState} over every mech combatant (or just `combatantIds`), and
 * gather the results into one roster batch plus a before → after row per committed mech.
 * A **keyed allied NPC** (`persistentNpcKey`, `decisions/0196`) is a candidate too: its wear goes
 * to `npcWear[key]` as one `apply_npc_wear` in the same batch, with a row in `npcRows`. An
 * unkeyed NPC is not a candidate; a mech the extractor won't commit, or a second combatant
 * carrying a key already committed, lands in `skipped`. A **pilot on foot** whose `sourceRef` names
 * a roster pilot carries its HP to `PilotData.currentHp` (`set_pilot_hp`, cleared at full), with
 * a row in `pilotRows`; one whose pilot no longer exists, or a second on the same pilot, is skipped. Pure — the caller applies `batch` (the
 * CLI `commit` verb, the GM's END COMBAT panel).
 *
 * `now` stamps the `record_encounter_commit`. The batch always carries it, even with no rows,
 * so an all-skipped commit still records that it ran.
 */
export function planCommit(args: {
  state: EncounterState;
  roster: RosterState;
  compendium: Compendium;
  encounterId: string;
  now: string;
  actorId: string;
  combatantIds?: string[];
}): CommitPlan {
  const { state, roster, compendium, encounterId, now, actorId, combatantIds } = args;
  const candidates = combatantIds
    ? combatantIds.flatMap((id) => state.combatants[id] ?? [])
    : Object.values(state.combatants).filter((c) => COMMITTED_KINDS.has(c.kind) || c.persistentNpcKey !== undefined);

  const rows: CommitRow[] = [];
  const npcRows: NpcCommitRow[] = [];
  const pilotRows: PilotCommitRow[] = [];
  const skipped: CommitPlanSkip[] = [];
  for (const c of candidates) {
    if (c.kind === "pilot") {
      const pilot = c.sourceRef !== undefined ? roster.pilots[c.sourceRef] : undefined;
      if (!pilot) {
        skipped.push({ combatantId: c.id, reason: `${c.name}: source pilot ${c.sourceRef ?? "(none)"} no longer exists (dangling — skipped)` });
        continue;
      }
      const first = pilotRows.find((r) => r.pilotId === pilot.id);
      if (first) {
        skipped.push({ combatantId: c.id, reason: `${c.name}: pilot ${pilot.id} is already committed from ${first.combatantName}` });
        continue;
      }
      const maxHp = c.base.maxHp;
      pilotRows.push({ combatantId: c.id, combatantName: c.name, pilotId: pilot.id, maxHp, before: pilot.currentHp ?? maxHp, after: c.current.hp });
      continue;
    }
    const key = c.persistentNpcKey;
    if (key !== undefined) {
      const first = npcRows.find((r) => r.key === key);
      if (first) {
        skipped.push({ combatantId: c.id, reason: `${c.name}: "${key}" is already committed from ${first.combatantName}` });
        continue;
      }
      const wear = npcWearOf(c);
      npcRows.push({ combatantId: c.id, combatantName: c.name, key, before: npcValues(roster.npcWear?.[key], c), after: npcValues(wear, c), wear });
      continue;
    }
    const payload = extractPrintedMechState({ combatant: c, roster, compendium });
    if (!payload.committed) {
      skipped.push({ combatantId: c.id, reason: payload.reason });
      continue;
    }
    // The extractor only commits when the pilot and its maxima resolve.
    const pilot = roster.pilots[payload.pilotId]!;
    const maxes = printedMechMaxes({ pilot, mechId: payload.mechId, compendium })!;
    rows.push({
      combatantId: c.id,
      combatantName: c.name,
      pilotId: payload.pilotId,
      mechId: payload.mechId,
      before: rowValues(pilot.printedMechs[payload.mechId], maxes),
      after: rowValues(payload, maxes),
      payload,
    });
  }

  const priorCommitAt = roster.encounterCommits?.[encounterId];
  return {
    batch: {
      type: "batch",
      commands: [
        { type: "record_encounter_commit", actorId, encounterId, at: now },
        ...rows.map((r) => applyCommand(actorId, r.payload)),
        ...npcRows.map((r): ApplyNpcWearCommand => ({ type: "apply_npc_wear", actorId, key: r.key, wear: r.wear })),
        ...pilotRows.map((r): SetPilotHpCommand => ({ type: "set_pilot_hp", actorId, pilotId: r.pilotId, ...(r.after < r.maxHp ? { hp: r.after } : {}) })),
      ],
    },
    rows,
    npcRows,
    pilotRows,
    skipped,
    ...(priorCommitAt !== undefined ? { priorCommitAt } : {}),
  };
}
